/* istanbul ignore file */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SortColumn,
  type Column,
  DataGridHandle,
} from "@bst/react-data-grid";
import {
  useLoadingRows,
  useColumnResize,
  DataGridPreferences,
} from "@bst/react-ui";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  OpenAPI,
  MachineLearningSettingsService,
  ProjectThresholdDTO,
  UserPreferencesDTO,
  ProjectDTO,
} from "../../../apiclients/bstinsights";
import useAcquireToken from "../../../components/AuthToken/Hooks/useAcquireToken";
import { SortableHeader } from "../../DataGrid/Views/SortableHeader";
import { ProjectNameInputCell } from "../Components/ProjectNameInputCell";
import { NLMValueInputCell } from "../Components/NLMValueInputCell";
import useBSTInsightsAPI from "../../../apiclients/api/bstinsightsapi";
import { useMsal } from "@azure/msal-react";
import { ComponentsEnum } from "../../../utils/components";
import { projectsForProjectThresholdsQueryKey } from './useProjectsForThresholds';

type CellPosition = {
  rowIdx: number;
  idx: number;
};

function useAddProjectOverride() {
  const { getTokenSilently } = useAcquireToken();
  const { mergeProjectThresholds } = useBSTInsightsAPI();

  return useMutation({
    mutationFn: async (nlmThreshold: ProjectThresholdDTO) => {
      const token = await getTokenSilently();
      if (!token) return;
      await mergeProjectThresholds(token, nlmThreshold);
    },
  });
}

function useUserPreferences() {
  const { getUserPreferences } = useBSTInsightsAPI();
  const { getTokenSilently } = useAcquireToken();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  return useQuery({
    queryKey: ["nlmSettings", "preferences"],
    queryFn: async () => {
      const token = await getTokenSilently();
      const userId = activeAccount?.localAccountId || "";
      const preferences: UserPreferencesDTO | undefined =
        await getUserPreferences(
          token,
          userId,
          ComponentsEnum.NLMProjectOverrideGrid
        );
      return JSON.parse(
        preferences?.settings || '{"columns":{}}'
      ) as DataGridPreferences<ProjectThresholdDTO>;
    },
  });
}

function useUpsertUserPreferences() {
  const { upsertUserPreferences } = useBSTInsightsAPI();
  const { getTokenSilently } = useAcquireToken();
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      preferences: DataGridPreferences<ProjectThresholdDTO>
    ) => {
      // Optimistically update the current preference
      queryClient.setQueryData<DataGridPreferences<ProjectThresholdDTO>>(
        ["nlmSettings", "preferences"],
        () => preferences
      );

      const token: string = await getTokenSilently();
      const userId = activeAccount?.localAccountId || "";

      const request: UserPreferencesDTO = {
        azureIdentityId: userId,
        componentName: ComponentsEnum.NLMProjectOverrideGrid,
        settings: JSON.stringify(preferences),
      };

      if (token) {
        await upsertUserPreferences(token, request);
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["nlmSettings", "preferences"],
      }),
  });
}

export function useNLMProjectOverridesGrid() {
  const ref = useRef<DataGridHandle>(null);
  const [pageSize] = useState<number>(8);
  const [renderKey, setRenderKey] = useState<number>(0);
  const [selectedCell, setSelectedCell] = useState<CellPosition>();
  const [showOverrideError, setShowOverrideError] = useState(false);

  const {
    handleColumnResize,
    handleColumnResizeStart,
    handleColumnResizeEnd: onColumnResizeEnd,
    withColumnResizeCellClass,
  } = useColumnResize();

  const { getTokenSilently } = useAcquireToken();
  const { mutate: createOverride } = useAddProjectOverride();
  const { mutate: upsertPreferences } = useUpsertUserPreferences();
  const { data: preferences } = useUserPreferences();

  useEffect(() => {
    // Only re-render the grid once preferences are loaded.
    setRenderKey((key) => (key < 2 ? key + 1 : key));
  }, [preferences]);

  const sort = useMemo<SortColumn | undefined>(() => {
    if (preferences?.sort) {
      return {
        columnKey: preferences.sort.column,
        direction: preferences.sort.direction,
      };
    }
  }, [preferences]);

  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["nlmSettings", pageSize, sort], [pageSize, sort]);

  const {
    data = [],
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const token = await getTokenSilently();

      if (!token) return [];
      OpenAPI.TOKEN = token;

      const data =
        await MachineLearningSettingsService.machineLearningSettingsGetProjectThresholdsPaged(
          pageParam,
          pageSize,
          sort?.columnKey,
          sort?.direction
        );

      return data ? data : [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const lastPageItem = lastPage?.[lastPage.length - 1];
      return lastPageItem?.rowNumber
        ? Number(lastPageItem.rowNumber)
        : undefined;
    },
    select: (data) => data.pages.flatMap((page) => page),
  });

  const isAtBottom = ({
    currentTarget,
  }: React.UIEvent<HTMLDivElement>): boolean => {
    return (
      currentTarget.scrollTop + 56 >=
      currentTarget.scrollHeight - currentTarget.clientHeight
    );
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!isFetching && hasNextPage && isAtBottom(event)) {
      fetchNextPage();
    }
  };

  const handleAddOverride = useCallback(() => {
    queryClient.setQueryData<InfiniteData<ProjectThresholdDTO[]>>(
      queryKey,
      (previousData) => {
        if (previousData) {
          const rows = previousData.pages.flatMap((page) => page);

          // Don't add a new row if an empty one exists
          if (rows.some((row) => row.projectId === -1 || row?.nlmThreshold === null || row?.nlmThreshold === undefined))
            return previousData;

          return {
            ...previousData,
            pages: previousData.pages.map((page, idx) =>
              idx === previousData.pages.length - 1
                ? [...page, { projectId: -1, projectName: "" }]
                : page
            ),
          };
        }
      }
    );

    const rows = queryClient
      .getQueryData<InfiniteData<ProjectThresholdDTO[]>>(queryKey)!
      .pages.flatMap((page) => page);

    const rowIdx = rows.findIndex((row) => row.projectId === -1);

    if (rowIdx !== -1) {
      // Auto-select cell
      setSelectedCell({ rowIdx, idx: 0 });
    }
  }, [queryClient, queryKey, setSelectedCell]);

  const handleCancelAddOverride = useCallback(() => {
    queryClient.setQueryData<InfiniteData<ProjectThresholdDTO[]>>(
      queryKey,
      (previousData) => {
        if (previousData) {
          return {
            ...previousData,
            pages: previousData.pages.map((page) =>
              page.filter(({ projectId }) => projectId !== -1)
            ),
          };
        }
      }
    );
  }, [queryClient, queryKey]);

  const handleCreateOverride = useCallback(
    (project: ProjectDTO) => {
      // Update the placeholder rowId and auto-select the nlm value cell
      queryClient.setQueryData<InfiniteData<ProjectThresholdDTO[]>>(
        queryKey,
        (previousData) => {
          if (previousData) {
            return {
              ...previousData,
              pages: previousData.pages.map((page) =>
                page.map((row) =>
                  row.projectId === -1
                    ? { projectId: project.projectId!, projectName: project.projectName! }
                    : row
                )
              ),
            };
          }
        }
      );

      // Select the next cell automatically
      const rows = queryClient
        .getQueryData<InfiniteData<ProjectThresholdDTO[]>>(queryKey)!
        .pages.flatMap((page) => page);

      const rowIdx = rows.findIndex((row) => row.projectId === project.projectId);

      if (rowIdx !== -1) setSelectedCell({ rowIdx, idx: 1 });
    },
    [queryClient, queryKey]
  );

  const handleNLMOverrideUpdate = useCallback((row: ProjectThresholdDTO) => {
    createOverride(row);
    // Update the cache optimistically
    queryClient.setQueryData<InfiniteData<ProjectThresholdDTO[]>>(
      queryKey,
      (previousData) => {
        if (previousData) {
          return {
            ...previousData,
            pages: previousData.pages.map((page) => {
              const rowIdx = page.findIndex(
                (r) => Number(r.projectId) === Number(row.projectId)
              );

              // Create a new array and update the row while maintaining order
              return rowIdx !== -1
                ? [...page.slice(0, rowIdx), row, ...page.slice(rowIdx + 1)]
                : page;
            }),
          };
        }
      }
    );
    Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ queryKey: [projectsForProjectThresholdsQueryKey] })
    ])
  }, [createOverride, queryClient, queryKey]);

  const handleColumnResizeEnd = (index: number, width: number) => {
    onColumnResizeEnd(index, width);
    const columnKey = columns[index].key;
    const columnPreferences =
      preferences?.columns?.[columnKey as keyof typeof preferences.columns];
    upsertPreferences({
      ...preferences,
      columns: {
        ...preferences?.columns,
        [columnKey]: {
          ...columnPreferences,
          width,
        },
      },
    });
  };

  const handleSortChange = useCallback(
    (columnKey: string) => {
      let nextSort: SortColumn | undefined;

      if (sort?.columnKey !== columnKey) {
        nextSort = { columnKey, direction: "ASC" };
      } else if (sort.direction === "ASC") {
        nextSort = { columnKey, direction: "DESC" };
      } else {
        nextSort = undefined;
      }

      if (nextSort) {
        upsertPreferences({
          ...(preferences ?? { columns: {} }),
          sort: {
            column: nextSort.columnKey as keyof ProjectThresholdDTO,
            direction: nextSort.direction,
          },
        });
      } else {
        upsertPreferences({
          ...(preferences ?? { columns: {} }),
          sort: undefined,
        });
      }
    },
    [sort, preferences, upsertPreferences]
  );

  const handleShowOverrideError = useCallback((projectId: number) => {
    const rowIdx = data.findIndex((row) => row.projectId === projectId);
    if (rowIdx >= 0) {
      setSelectedCell({ rowIdx, idx: 1 })
    }
    setShowOverrideError(true)
  }, [data]);

  const handleNLMErrorModalClose = () => {
    setShowOverrideError(false);
    setTimeout(() => {
      if (selectedCell) {
        ref.current?.selectCell(selectedCell, true);
        ref.current?.scrollToCell(selectedCell);
      }
    }, 200)
  }

  useEffect(() => {
    if (selectedCell) {
      ref.current?.scrollToCell(selectedCell);
      ref.current?.selectCell(selectedCell, true);
    }
  }, [selectedCell]);

  const rows = useLoadingRows({
    rows: data,
    loadingRowsCount: 8,
    isLoading: isFetching,
  });

  const initialColumns = useMemo<Column<ProjectThresholdDTO>[]>(
    () =>
      withColumnResizeCellClass([
        {
          key: "projectName",
          accessor: "projectName",
          name: "Name",
          editable: (row) => row.projectId === -1,
          renderHeaderCell: (props) => (
            <SortableHeader
              {...props}
              onToggleSort={() => handleSortChange(props.column.key)}
            />
          ),
          renderEditCell: (props) => (
            <ProjectNameInputCell
              {...props}
              onCancelAddOverride={handleCancelAddOverride}
              onSelectProject={handleCreateOverride}
            />
          ),
          renderCell: ({ row }) => (
            <span data-testid="projectName">{row.projectName}</span>
          ),
        },
        {
          key: "nlmThreshold",
          accessor: "nlmThreshold",
          name: "NLM Override Value",
          editable: true,
          cellClass: "nlm-threshold-cell",
          editorOptions: { commitOnOutsideClick: true },
          renderHeaderCell: (props) => (
            <SortableHeader
              {...props}
              onToggleSort={() => handleSortChange(props.column.key)}
            />
          ),
          renderEditCell: (props) => (
            <NLMValueInputCell
              {...props}
              cancelChanges={handleCancelAddOverride}
              showOverrideError={() => handleShowOverrideError(props.row.projectId!)}
              updateNLMOverride={handleNLMOverrideUpdate}
            />
          ),
          renderCell: (props) => props.row.nlmThreshold?.toFixed(2),
        },
      ]),
    [handleCancelAddOverride, handleCreateOverride, handleNLMOverrideUpdate, handleShowOverrideError, handleSortChange, withColumnResizeCellClass]
  );

  const columns = initialColumns.map((column) => ({
    ...column,
    ...("key" in column
      ? preferences?.columns?.[column.key as keyof typeof preferences.columns]
      : {}),
  }));

  return {
    renderKey,
    ref,
    rows,
    columns,
    sort,
    selectedCell,
    setSelectedCell,
    handleScroll,
    handleSortChange,
    handleAddOverride,
    handleCancelAddOverride,
    handleColumnResize,
    handleColumnResizeStart,
    handleColumnResizeEnd,
    handleCreateOverride,
    showOverrideError,
    handleNLMErrorModalClose
  };
}
